import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import usePageTitle from '../../hooks/usePageTitle';
import DashboardShell from "../../components/Dashboard/DashboardShell";
import LoadingSpin from "../../components/Dashboard/LoadingSpin";
import http from "../../http-common";
import { getMyCollaborations } from "../../api/epks";
import { useTranslation } from "react-i18next";

import moviePic from "../../images/movie11.jpg";

import GenderDropdown from "../../components/Filter/GenderDropdown";
import AgeRangeDropdown from "../../components/Filter/AgeRangeDropdown";
import { AGE_OPTIONS } from "../../constants/AgeOptions";
import { ageInRange } from "../../utils/ageRange";
import EthnicityDropdown from "../../components/Filter/EthnicityDropdown";
import FilterWrapper from "../../components/Filter/FilterWrapper";

export default function SavedPage() {
  usePageTitle('Saved');
  const { t } = useTranslation();
  const user = useSelector((state) => state.user);
  const userId = user?.id || "0";
  const userRole = user?.role || "User";

  const [starEpkList, setStarEpkList] = useState([]);
  const [followEpkList, setFollowEpkList] = useState([]);
  const [buyEpkList, setBuyEpkList] = useState([]);
  const [actorFollowedList, setActorFollowedList] = useState([]);
  const [actorStarredList, setActorStarredList] = useState([]);
  const [filteredEpkList, setFilteredEpkList] = useState([]);
  const [filteredActorList, setFilteredActorList] = useState([]);
  const [collaborationList, setCollaborationList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [productionFilter, setProductionFilter] = useState(0);
  const [itemFilter, setItemFilter] = useState(0);
  const [typeFilter, setTypeFilter] = useState(0);

  const [selectedGender, setSelectedGender] = useState("All");
  const [selectedAgeRange, setSelectedAgeRange] = useState("All");
  const [selectedEthnicity, setSelectedEthnicity] = useState("All");
  const [ageLabel, setAgeLabel] = useState("All");
  const [ethnicityLabel, setEthnicityLabel] = useState("All");

  useEffect(() => {
    if (userId === "0") return;

    setLoading(true);
    // allSettled so one failed list (e.g. a 403 on the actor routes) does not blank the other four.
    Promise.allSettled([
      http.get(`/fepks/getStarredFepksByUser/${userId}`),
      http.get(`/fepks/getFollowingFepksByUser/${userId}`),
      http.get(`/fepks/getWishTobuyByUser/${userId}`),
      http.get(`/users/followed/${userId}`),
      http.get(`/users/starred/${userId}`),
    ]).then((results) => {
      const [starFepk, followFepk, buyFepk, actorsFollowed, actorsStarred] = results.map((r) => {
        if (r.status === "rejected") console.error("Fetch Error:", r.reason);
        return r.status === "fulfilled" && Array.isArray(r.value.data) ? r.value.data : [];
      });
      setStarEpkList(starFepk);
      setFollowEpkList(followFepk);
      setBuyEpkList(buyFepk);
      setActorFollowedList(actorsFollowed);
      setActorStarredList(actorsStarred);
      setLoading(false);
    });

    if (user?.token) {
      getMyCollaborations(user.token)
        .then((res) => setCollaborationList(Array.isArray(res) ? res : []))
        .catch((err) => console.error("Collaborations fetch error:", err));
    }
  }, [userId, user?.token]);

  useEffect(() => {
    if (itemFilter === 0) {
      let prodStatus = productionFilter === 1 ? ["Preproduction"] :
                       productionFilter === 2 ? ["Production"] :
                       productionFilter === 3 ? ["Postproduction"] :
                       null;

      let source = typeFilter === 1 ? starEpkList :
                   typeFilter === 2 ? followEpkList :
                   typeFilter === 3 ? buyEpkList :
                   [...starEpkList, ...followEpkList, ...buyEpkList];

      const unique = Array.from(new Map(source.map(item => [item._id, item])).values());
      setFilteredEpkList(prodStatus ? unique.filter(epk => prodStatus.includes(epk.status)) : unique);
    } else {
      let source = typeFilter === 1 ? actorStarredList :
                   typeFilter === 2 ? actorFollowedList :
                   [...actorStarredList, ...actorFollowedList];

      const unique = Array.from(new Map(source.map(item => [item._id, item])).values());

      setFilteredActorList(unique.filter(actor => {
        const matchGender = selectedGender === "All" || actor.gender === selectedGender;
        const matchAge = selectedAgeRange === "All" || ageInRange(actor.age, selectedAgeRange);
        const matchEthnicity = selectedEthnicity === "All" || actor.ethnicity === selectedEthnicity;
        return matchGender && matchAge && matchEthnicity;
      }));
    }
  }, [itemFilter, typeFilter, productionFilter, selectedGender, selectedAgeRange, selectedEthnicity, starEpkList, followEpkList, buyEpkList, actorFollowedList, actorStarredList]);

  const handleItemBtnClick = (index) => {
    setItemFilter(index);
    if (index === 1 && typeFilter !== 1 && typeFilter !== 2) {
      setTypeFilter(0);
    } else if (index === 0) {
      setTypeFilter(0);
    }
  };
  const handleNumBtnClick = (index) => setTypeFilter(index);
  const handleBtnClick = (index) => setProductionFilter(index);
  const handleGenderSelect = (gender) => setSelectedGender(gender);
  // AgeRangeDropdown passes only the value (FilterTag relies on that), so look up the label here.
  const handleAgeRangeSelect = (value) => {
    setSelectedAgeRange(value);
    setAgeLabel(AGE_OPTIONS.find((o) => o.value === value)?.label ?? value);
  };
  const handleEthnicitySelect = (label, value) => { setSelectedEthnicity(value); setEthnicityLabel(label); };

  const selectClass = "tw-w-full tw-rounded-lg tw-border tw-border-white/20 tw-bg-[#190033] tw-px-3 tw-py-2 tw-text-sm tw-text-white tw-cursor-pointer focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-[#712CB0]";

  return (
    <DashboardShell role={userRole} pageTitle={t("Saved")}>
      {/* Header row: description + item toggle */}
      <div className="tw-mb-4 tw-flex tw-flex-col tw-gap-4 sm:tw-flex-row sm:tw-items-center sm:tw-justify-between">
        <p className="tw-text-sm tw-text-white/50 tw-m-0">
          {itemFilter === 0 ? t("Find all the film EPKs you've saved here.") : itemFilter === 1 ? t("Find all the actors you've saved here.") : t("EPKs you've been invited to collaborate on.")}
        </p>
        <div className="tw-flex tw-gap-1 tw-rounded-xl tw-bg-white/10 tw-p-1">
          {[t("Film EPKs"), t("Actors"), t("Shared with me")].map((label, i) => (
            <button
              key={label}
              onClick={() => handleItemBtnClick(i)}
              className={`tw-rounded-lg tw-px-4 tw-py-1.5 tw-text-sm tw-font-medium tw-transition-colors tw-border-0 tw-cursor-pointer ${
                itemFilter === i
                  ? "tw-bg-[#712CB0] tw-text-white tw-shadow"
                  : "tw-bg-transparent tw-text-white/50 hover:tw-text-white"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Filter row */}
      {itemFilter !== 2 && (
        itemFilter === 0 ? (
          <div className="tw-mb-4 tw-flex tw-flex-col tw-gap-3 sm:tw-flex-row">
            <select value={typeFilter} onChange={(e) => handleNumBtnClick(Number(e.target.value))} className={selectClass}>
              <option value={0}>{t("All types")}</option>
              <option value={1}>{t("Starred")}</option>
              <option value={2}>{t("Following")}</option>
              <option value={3}>{t("Wish to Buy")}</option>
            </select>
            <select value={productionFilter} onChange={(e) => handleBtnClick(Number(e.target.value))} className={selectClass}>
              <option value={0}>{t("All stages")}</option>
              <option value={1}>{t("Pre-Production")}</option>
              <option value={2}>{t("Production")}</option>
              <option value={3}>{t("Post-Production")}</option>
            </select>
          </div>
        ) : (
          <div className="tw-mb-4 tw-flex tw-flex-col tw-gap-3 sm:tw-flex-row sm:tw-items-center">
            <select value={typeFilter} onChange={(e) => handleNumBtnClick(Number(e.target.value))} className={selectClass}>
              <option value={0}>{t("All types")}</option>
              <option value={1}>{t("Starred")}</option>
              <option value={2}>{t("Following")}</option>
            </select>
            <FilterWrapper label={t("Gender")} selectedValue={selectedGender}>
              <GenderDropdown selectedValue={selectedGender} onOptionSelect={handleGenderSelect} />
            </FilterWrapper>
            <FilterWrapper label={t("Ethnicity")} selectedValue={ethnicityLabel !== "All" ? t("ethnicities." + ethnicityLabel) : "All"} align="right">
              <EthnicityDropdown selectedValue={selectedEthnicity} onOptionSelect={handleEthnicitySelect} />
            </FilterWrapper>
            <FilterWrapper label={t("Age")} selectedValue={ageLabel} align="right">
              <AgeRangeDropdown selectedValue={selectedAgeRange} onOptionSelect={handleAgeRangeSelect} />
            </FilterWrapper>
            {(selectedGender !== "All" || selectedAgeRange !== "All" || selectedEthnicity !== "All") && (
              <button
                onClick={() => { setSelectedGender("All"); setSelectedAgeRange("All"); setSelectedEthnicity("All"); setAgeLabel("All"); setEthnicityLabel("All"); }}
                className="tw-whitespace-nowrap tw-rounded-lg tw-border tw-border-white/20 tw-px-3 tw-py-2 tw-text-sm tw-text-white/50 hover:tw-border-white/40 hover:tw-text-white tw-bg-transparent tw-cursor-pointer"
              >
                {t("Clear")}
              </button>
            )}
          </div>
        )
      )}

      {/* Content grid */}
      <div className="tw-rounded-xl tw-bg-[#280D41] tw-border tw-border-white/10 tw-p-4">
        {loading ? (
          <LoadingSpin />
        ) : (
          <>
            {itemFilter === 2 ? (
              !Array.isArray(collaborationList) || collaborationList.length === 0 ? (
                <div className="tw-flex tw-h-full tw-items-center tw-justify-center tw-py-20">
                  <p className="tw-text-xl tw-font-light tw-text-white/40 tw-m-0">{t("No EPKs shared with you yet.")}</p>
                </div>
              ) : (
                <div className="tw-grid tw-grid-cols-2 tw-gap-4 tw-p-2 md:tw-grid-cols-4 lg:tw-grid-cols-6 xl:tw-grid-cols-8">
                  {collaborationList.map((epk) => {
                    const imgSrc = epk.banner_url
                      ? `${process.env.REACT_APP_AWS_URL}/${epk.banner_url}`
                      : moviePic;
                    return (
                      <a key={epk._id} href={`/epk/${epk._id}?edit=true`} className="tw-transition-transform hover:tw-scale-105">
                        <img src={imgSrc} alt={epk.title} className="tw-aspect-square tw-w-full tw-rounded-lg tw-object-cover" />
                        <p className="tw-mt-1 tw-truncate tw-text-center tw-text-xs tw-text-white/70">{epk.title}</p>
                      </a>
                    );
                  })}
                </div>
              )
            ) : (
              ((itemFilter === 0 && filteredEpkList.length === 0) || (itemFilter === 1 && filteredActorList.length === 0)) ? (
                <div className="tw-flex tw-h-full tw-items-center tw-justify-center tw-py-20">
                  <p className="tw-text-xl tw-font-light tw-text-white/40 tw-m-0">
                    {itemFilter === 0 ? t("No EPKs found matching your search.") : t("No Actors found matching your search.")}
                  </p>
                </div>
              ) : (
                <div className="tw-grid tw-grid-cols-2 tw-gap-4 tw-p-2 md:tw-grid-cols-4 lg:tw-grid-cols-6 xl:tw-grid-cols-8">
                  {(itemFilter === 0 ? filteredEpkList : filteredActorList).map((item) => {
                    const isEpk = itemFilter === 0;
                    const linkPath = isEpk ? `/epk/${item._id}` : `/user/${item._id}`;
                    const imgSrc = isEpk
                      ? (item.banner_url ? `${process.env.REACT_APP_AWS_URL}/${item.banner_url}` : moviePic)
                      : (item.picture?.startsWith("https") ? item.picture : `${process.env.REACT_APP_AWS_URL}/${item.picture}`);
                    return (
                      <a key={item._id} href={linkPath} className="tw-transition-transform hover:tw-scale-105">
                        <img
                          src={imgSrc}
                          alt={isEpk ? item.title : `${item.firstName} ${item.lastName}`}
                          className={`tw-aspect-square tw-w-full tw-object-cover ${isEpk ? "tw-rounded-lg" : "tw-rounded-3xl"}`}
                        />
                        {!isEpk && (
                          <div className="tw-mt-1 tw-w-full tw-truncate tw-text-center">
                            <p className="tw-text-sm tw-font-medium tw-text-white/70 tw-m-0">{`${item.firstName} ${item.lastName}`}</p>
                          </div>
                        )}
                      </a>
                    );
                  })}
                </div>
              )
            )}
          </>
        )}
      </div>
    </DashboardShell>
  );
}
